import type { TuningAction, TuningCapabilities, TuningCommand, TuningExperimentControl, TuningExperimentSnapshot,
  TuningIdentity, TuningReceipt, TuningTelemetryWindow } from '@playcastdotio/PeerCore/tuning';
export type StreamTuningView = { armed: boolean; guest?: TuningCapabilities; host?: TuningCapabilities;
  target?: TuningIdentity; leaseId?: string; latestGuest?: TuningTelemetryWindow; latestHost?: TuningTelemetryWindow;
  receipt?: TuningReceipt; experiment?: TuningExperimentSnapshot };
export interface IStreamTuningClientPort {
  snapshot(): StreamTuningView; subscribe(listener: () => void): () => void;
  sendCommand(command: TuningCommand): boolean; sendExperiment(control: TuningExperimentControl): boolean; exportSnapshot(): unknown;
}
type Pending = { resolve: (receipt: TuningReceipt) => void; reject: (error: Error) => void; timer: ReturnType<typeof setTimeout> };

/** Typed native-channel adapter. A remote page never addresses the host loopback API. */
export class StreamTuningService {
  private port?: IStreamTuningClientPort;
  private unbind?: () => void;
  private readonly listeners = new Set<() => void>();
  private readonly pending = new Map<string, Pending>();
  constructor(private readonly id: () => string = () => crypto.randomUUID()) {}
  bind(port: IStreamTuningClientPort | null): void {
    this.unbind?.(); this.unbind = undefined; this.port = port ?? undefined;
    if (port) this.unbind = port.subscribe(() => this.changed());
    if (!port) for (const [id, pending] of this.pending) { clearTimeout(pending.timer); pending.reject(new Error('Guest tuning connection ended')); this.pending.delete(id); }
    this.changed();
  }
  snapshot(): StreamTuningView { return this.port?.snapshot() ?? { armed: false }; }
  subscribe(listener: () => void): () => void { this.listeners.add(listener); return () => { this.listeners.delete(listener); }; }
  execute(action: TuningAction): Promise<TuningReceipt> {
    const view = this.snapshot();
    if (!view.armed || !view.target || !view.leaseId || !this.port) return Promise.reject(new Error('Host tuning lease is not armed'));
    if (this.pending.size) return Promise.reject(new Error('Another tuning command is pending'));
    const command: TuningCommand = { version: 1, commandId: this.id(), target: { ...view.target }, expectedRevision: view.target.configRevision,
      leaseId: view.leaseId, ttlMs: 10_000, action };
    const port = this.port;
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => { this.pending.delete(command.commandId); reject(new Error('Host acknowledgment timed out; check the recorded trial before retrying')); }, 20_000);
      this.pending.set(command.commandId, { resolve, reject, timer });
      if (!port.sendCommand(command)) { clearTimeout(timer); this.pending.delete(command.commandId); reject(new Error('Tuning control channel is unavailable')); }
    });
  }
  experiment(control: TuningExperimentControl): void {
    if (!this.port?.sendExperiment(control)) throw new Error('Experiment control channel is unavailable');
  }
  exportSnapshot(): unknown { return this.port?.exportSnapshot() ?? { snapshot: this.snapshot() }; }
  private changed(): void {
    const receipt = this.snapshot().receipt;
    if (receipt && ['applied', 'failed', 'rejected'].includes(receipt.stage)) {
      const pending = this.pending.get(receipt.commandId);
      if (pending) { clearTimeout(pending.timer); this.pending.delete(receipt.commandId); pending.resolve(receipt); }
    }
    for (const listener of this.listeners) listener();
  }
}
export const streamTuningService = new StreamTuningService();
